import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./DashboardLayout.css";

const IconUsers = (p) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 19c.6-3.4 3-5.2 5.5-5.2s4.9 1.8 5.5 5.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M15.5 6.2a3 3 0 0 1 0 5.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M17.5 13.9c2.1.5 3.6 2.1 4 4.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconGallery = (p) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <rect x="3" y="4.5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.3" cy="9.5" r="1.6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4 17l5.2-5.2a1.6 1.6 0 0 1 2.2 0L15 15.4M14 14.4l1.6-1.6a1.6 1.6 0 0 1 2.2 0L20 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconSeo = (p) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M15.3 15.3L20 20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconLogout = (p) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 16l4-4-4-4M20 12H9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconMenu = (p) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const NAV_ITEMS = [
  { to: "/dashboard/users", label: "Users", icon: IconUsers },
  { to: "/dashboard/gallery", label: "Gallery", icon: IconGallery },
];

// Roles that are NOT allowed to open Users / Gallery from the sidebar.
// Change this list if the restriction should apply to more roles later.
const RESTRICTED_ROLES = [3];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const isRestricted = RESTRICTED_ROLES.includes(user?.role);

  function handleLogout() {
    setConfirmingLogout(false);
    logout();
  }

  function handleNavClick(e) {
    if (isRestricted) {
      e.preventDefault();
      alert("You cannot access this page.");
      return;
    }
    setSidebarOpen(false);
  }

  return (
    <div className="dlayout">
      {/* ---------------- SIDEBAR ---------------- */}
      <aside className={`dlayout__sidebar ${sidebarOpen ? "dlayout__sidebar--open" : ""}`}>
        <div className="dlayout__brand">
          <span className="dlayout__brand-mark">VP</span>
          <span className="dlayout__brand-word">FITNESS<em>Admin</em></span>
        </div>

        <nav className="dlayout__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `dlayout__navlink ${isActive ? "dlayout__navlink--active" : ""} ${isRestricted ? "dlayout__navlink--locked" : ""}`
              }
              onClick={handleNavClick}
              aria-disabled={isRestricted}
            >
              <span className="dlayout__navlink-rail" />
              <item.icon className="icon" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="dlayout__sidebar-foot">
          <div className="dlayout__user-chip">
            <span className="dlayout__user-avatar">{(user?.name || user?.username || "?").charAt(0).toUpperCase()}</span>
            <div>
              <div className="dlayout__user-name">{user?.username || "Admin"}</div>
              <div className="dlayout__user-role">{user?.isSuper ? "Super Admin" : "Staff"}</div>
            </div>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="dlayout__scrim" onClick={() => setSidebarOpen(false)} />}

      {/* ---------------- MAIN COLUMN ---------------- */}
      <div className="dlayout__main">
        <header className="dlayout__topbar">
          <button
            className="dlayout__menu-btn"
            aria-label="Toggle sidebar"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <IconMenu className="icon" />
          </button>

          <NavLink
            to="/dashboard/seo"
            className={({ isActive }) => `dlayout__seo-link ${isActive ? "dlayout__seo-link--active" : ""}`}
          >
            <IconSeo className="icon" />
            <span>SEO Content</span>
          </NavLink>

          <div className="dlayout__topbar-spacer" />

          {!confirmingLogout ? (
            <button className="dlayout__logout" onClick={() => setConfirmingLogout(true)}>
              <IconLogout className="icon" />
              <span>Logout</span>
            </button>
          ) : (
            <div className="dlayout__logout-confirm">
              <span>Log out?</span>
              <button className="dlayout__logout-confirm-yes" onClick={handleLogout}>Yes</button>
              <button className="dlayout__logout-confirm-no" onClick={() => setConfirmingLogout(false)}>Cancel</button>
            </div>
          )}
        </header>

        <main className="dlayout__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}